import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useAddDriveRoot, useUpdateDriveRoot } from '@/hooks';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import {
  DRIVE_ROOT_ITEM_ALIAS,
  DRIVE_ROOT_ROLE_EXPLANATIONS,
  DRIVE_ROOT_ROLES,
  type DriveRootFiltersInput,
  type DriveRootRoleValue,
  type DriveRootSafe,
} from '@vivport/backend/types';
import { Loader2, X } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';
import { toast } from 'sonner';
import { DRIVE_FOLDER_ROOT_CRUMB, DriveFolderPicker, type DriveFolderCrumb } from './DriveFolderPicker';

type RootDialogProps = {
  driveId: number;
  driveName: string;
  root: DriveRootSafe | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type FilterFormState = {
  modifiedAfter: string;
  includeExtensions: string[];
  excludeExtensions: string[];
  maxSizeMb: string;
  excludePathGlobs: string;
};

const BYTES_PER_MB = 1024 * 1024;

function initialFilters(root: DriveRootSafe | null): FilterFormState {
  return {
    modifiedAfter: root?.modifiedAfter ? root.modifiedAfter.slice(0, 10) : '',
    includeExtensions: root?.includeExtensions ?? [],
    excludeExtensions: root?.excludeExtensions ?? [],
    maxSizeMb: root?.maxSizeBytes ? String(Math.round(root.maxSizeBytes / BYTES_PER_MB)) : '',
    excludePathGlobs: root?.excludePathGlobs.join('\n') ?? '',
  };
}

function toFiltersInput(form: FilterFormState): DriveRootFiltersInput {
  const maxSizeMb = Number.parseFloat(form.maxSizeMb);
  return {
    modifiedAfter: form.modifiedAfter.trim().length > 0 ? form.modifiedAfter.trim() : null,
    includeExtensions: form.includeExtensions,
    excludeExtensions: form.excludeExtensions,
    maxSizeBytes: Number.isFinite(maxSizeMb) && maxSizeMb > 0 ? Math.round(maxSizeMb * BYTES_PER_MB) : null,
    excludePathGlobs: form.excludePathGlobs
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0),
  };
}

function ExtensionChips({
  id,
  label,
  values,
  onChange,
}: {
  id: string;
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const ext = draft.trim().toLowerCase().replace(/^\./, '');
    if (ext.length === 0) {
      return;
    }
    if (!values.includes(ext)) {
      onChange([...values, ext]);
    }
    setDraft('');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commit();
    }
  };

  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex flex-wrap gap-1">
        {values.map((ext) => (
          <Badge key={ext} variant="secondary" className="gap-1">
            {ext.toUpperCase()}
            <button
              type="button"
              aria-label={`Remove ${ext}`}
              onClick={() => onChange(values.filter((value) => value !== ext))}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      <Input
        id={id}
        value={draft}
        placeholder="pdf, docx… press Enter to add"
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
      />
    </div>
  );
}

export function RootDialog({ driveId, driveName, root, open, onOpenChange }: RootDialogProps) {
  const isEdit = root !== null;
  const addRoot = useAddDriveRoot();
  const updateRoot = useUpdateDriveRoot();
  const [crumbs, setCrumbs] = useState<DriveFolderCrumb[]>([DRIVE_FOLDER_ROOT_CRUMB]);
  const [role, setRole] = useState<DriveRootRoleValue>(root?.role ?? 'PROJECTS');
  const [filters, setFilters] = useState<FilterFormState>(() => initialFilters(root));
  const [backgroundExtraction, setBackgroundExtraction] = useState<boolean>(root?.backgroundExtraction ?? false);

  const busy = addRoot.isPending || updateRoot.isPending;
  const selected = crumbs[crumbs.length - 1];

  const handleSubmit = () => {
    const filtersInput = toFiltersInput(filters);
    if (isEdit && root) {
      updateRoot.mutate(
        { rootId: root.id, body: { filters: filtersInput, backgroundExtraction } },
        {
          onSuccess: (updated) => {
            toast.success(`Updated root ${updated.path}.`);
            onOpenChange(false);
          },
          onError: (error: unknown) => {
            toast.error(getAxiosErrorDisplayMessage(error, 'Failed to update the root.'));
          },
        }
      );
      return;
    }
    addRoot.mutate(
      {
        driveId,
        body: {
          providerItemId: selected.id ?? DRIVE_ROOT_ITEM_ALIAS,
          role,
          filters: filtersInput,
          backgroundExtraction,
        },
      },
      {
        onSuccess: (created) => {
          toast.success(`Added ${created.role} root ${created.path}.`);
          onOpenChange(false);
        },
        onError: (error: unknown) => {
          toast.error(getAxiosErrorDisplayMessage(error, 'Failed to add the root.'));
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit root ${root.path}` : `Add root to ${driveName}`}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Change the filters or background extraction. Change the role by removing and re-adding the root.'
              : 'Pick a folder, choose how its files are organised, and optionally narrow what is referenced.'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-5">
          {!isEdit ? (
            <DriveFolderPicker driveId={driveId} crumbs={crumbs} onCrumbsChange={setCrumbs} />
          ) : null}

          <div className="space-y-2">
            <Label>Role</Label>
            {isEdit ? (
              <p className="text-sm">
                <span className="font-medium">{root.role}</span>
                <span className="ml-2 text-muted-foreground">{DRIVE_ROOT_ROLE_EXPLANATIONS[root.role]}</span>
              </p>
            ) : (
              <RadioGroup value={role} onValueChange={(value) => setRole(value as DriveRootRoleValue)}>
                {DRIVE_ROOT_ROLES.map((candidate) => (
                  <div key={candidate} className="flex items-center space-x-2">
                    <RadioGroupItem value={candidate} id={`root-role-${candidate}`} />
                    <Label htmlFor={`root-role-${candidate}`}>
                      {candidate.charAt(0) + candidate.slice(1).toLowerCase()}
                      <span className="ml-2 text-xs text-muted-foreground">
                        {DRIVE_ROOT_ROLE_EXPLANATIONS[candidate]}
                      </span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="root-modified-after">Modified after</Label>
              <Input
                id="root-modified-after"
                type="date"
                value={filters.modifiedAfter}
                onChange={(event) => setFilters({ ...filters, modifiedAfter: event.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="root-max-size">Max size (MB)</Label>
              <Input
                id="root-max-size"
                type="number"
                min={1}
                value={filters.maxSizeMb}
                onChange={(event) => setFilters({ ...filters, maxSizeMb: event.target.value })}
              />
            </div>
            <ExtensionChips
              id="root-include-ext"
              label="Include extensions"
              values={filters.includeExtensions}
              onChange={(next) => setFilters({ ...filters, includeExtensions: next })}
            />
            <ExtensionChips
              id="root-exclude-ext"
              label="Exclude extensions"
              values={filters.excludeExtensions}
              onChange={(next) => setFilters({ ...filters, excludeExtensions: next })}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="root-exclude-globs">Exclude path globs (one per line)</Label>
            <Textarea
              id="root-exclude-globs"
              rows={3}
              placeholder="**/Archive/**"
              value={filters.excludePathGlobs}
              onChange={(event) => setFilters({ ...filters, excludePathGlobs: event.target.value })}
            />
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label htmlFor="root-background-extraction">Background extraction</Label>
              <p className="text-xs text-muted-foreground">
                Extract text overnight so Neo can search inside these documents
              </p>
            </div>
            <Switch
              id="root-background-extraction"
              checked={backgroundExtraction}
              onCheckedChange={setBackgroundExtraction}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEdit ? 'Save' : 'Add root'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
